import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, euroFormat, resolveImageUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FalkeCoinLogo } from "../../components/Brand";
import Leaderboard from "../../components/Leaderboard";
import { STATUS_COLORS, STUFE_FARBE } from "../../lib/constants";
import { ArrowRight, Coins, Gift, Copy, QrCode, TrendingUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { fireConfetti } from "../../components/Confetti";
import { QRCodeSVG } from "qrcode.react";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/dashboard").then((r) => setData(r.data));
  }, []);

  const empfehlungsLink = `${window.location.origin}/r/${user.empfehlungs_link_slug}`;

  if (user.tippgeber_modus === "gewerblich_aktiv") {
    return <div data-testid="redirect-cockpit" className="text-falke-blue">Bitte über Cockpit navigieren.</div>;
  }

  if (!data) return <div className="text-falke-blue">Lade Dashboard…</div>;

  const naechste = data.naechste_stufe;
  const progress = naechste
    ? Math.min(100, Math.round(((naechste.schwelle - naechste.fehlend) / naechste.schwelle) * 100))
    : 100;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(empfehlungsLink);
      setCopied(true);
      fireConfetti("soft");
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      {/* Greeting */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-falke-blue">Hallo {user.name.split(" ")[0]} ☀️</h1>
        <p className="text-falke-anthracite/70 mt-1">Schön, dass du da bist. So steht's um dein FalkeCoin-Konto.</p>
      </div>

      {/* Balance hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2D5C7E] via-[#264D6A] to-[#1C425E] text-white p-7 shadow-[0_12px_40px_rgba(45,92,126,0.18)]">
        <div className="absolute -top-10 -right-6 opacity-90">
          <FalkeCoinLogo size={180} glow animate />
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-xs uppercase tracking-[0.18em] text-falke-gold mb-2">Dein FalkeCoin-Konto</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={data.verfuegbare_coins}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="font-heading text-5xl sm:text-6xl font-bold tabular text-falke-gold balance-flash"
              data-testid="coin-balance"
            >
              <CoinCountUp value={data.verfuegbare_coins} /> <span className="text-2xl text-white/80 font-medium">FC</span>
            </motion.div>
          </AnimatePresence>
          <div className="text-white/80 mt-1">= {euroFormat(data.verfuegbare_coins)} an Sachprämien</div>
          {data.stufe && (
            <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur" data-testid="stufe-badge">
              <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${STUFE_FARBE[data.stufe]}`} />
              <span className="font-semibold text-sm">{data.stufe}-Stufe</span>
            </div>
          )}
        </div>

        <Link to="/app/empfehlen" className="absolute bottom-6 right-6 hidden sm:inline-flex items-center gap-2 bg-falke-orange hover:bg-falke-orange-dark text-white font-semibold px-5 py-3 rounded-xl shadow-lg" data-testid="cta-empfehlen-desktop">
          Jetzt empfehlen <ArrowRight size={18} />
        </Link>
      </div>
      <Link to="/app/empfehlen" className="sm:hidden btn-primary w-full text-center" data-testid="cta-empfehlen-mobile">Jetzt empfehlen</Link>

      {/* Progress */}
      {naechste && (
        <div className="falke-card p-6" data-testid="progress-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs text-falke-blue/60 uppercase tracking-wider">Fortschritt</div>
              <div className="font-heading text-lg text-falke-blue">
                {data.empfehlungen_lifetime} von {naechste.schwelle} zur {naechste.name}-Stufe
              </div>
            </div>
            <TrendingUp className="text-falke-orange" size={22} />
          </div>
          <div className="w-full h-3 rounded-full bg-falke-blue/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-falke-orange to-falke-gold"
            />
          </div>
          <div className="text-xs text-falke-anthracite/60 mt-2">Noch {naechste.fehlend} {naechste.fehlend === 1 ? "Empfehlung" : "Empfehlungen"} bis zur nächsten Stufe.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Empfehlungen */}
        <div className="falke-card p-6" data-testid="recent-empfehlungen">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-falke-blue">Deine Empfehlungen</h3>
            <Link to="/app/empfehlen?tab=list" className="text-sm text-falke-orange font-semibold">Alle ansehen</Link>
          </div>
          {data.empfehlungen.length === 0 ? (
            <div className="text-sm text-falke-anthracite/60 py-8 text-center">
              Noch keine Empfehlung. Starte jetzt und sammle Coins.
            </div>
          ) : (
            <ul className="space-y-3">
              {data.empfehlungen.slice(0, 4).map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3" data-testid={`empfehlung-${e.id}`}>
                  <div>
                    <div className="font-medium text-falke-anthracite">{e.empfohlener_name}</div>
                    <div className="text-xs text-falke-anthracite/60">{e.notiz || "—"}</div>
                  </div>
                  <span className={`status-badge ${STATUS_COLORS[e.status] || "bg-gray-100 text-gray-700"}`}>{e.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Link + QR */}
        <div className="falke-card p-6" data-testid="referral-link-card">
          <h3 className="font-heading text-lg text-falke-blue mb-2">Dein Empfehlungs-Link</h3>
          <p className="text-xs text-falke-anthracite/60 mb-4">Teile diesen Link oder QR-Code und sammle Coins für jede erfolgreiche Empfehlung.</p>
          <div className="flex gap-2">
            <input readOnly value={empfehlungsLink} className="flex-1 px-3 py-2 rounded-xl border border-falke-blue/15 text-sm bg-falke-warmwhite" data-testid="referral-link-input" />
            <button onClick={copyLink} className="btn-secondary !py-2 !px-3" data-testid="copy-link-btn">
              <Copy size={16} />
            </button>
            <button onClick={() => setShowQR(!showQR)} className="btn-outline !py-2 !px-3" data-testid="show-qr-btn">
              <QrCode size={16} />
            </button>
          </div>
          {copied && <div className="text-xs text-falke-success mt-2" data-testid="link-copied">Link kopiert ✓</div>}
          {showQR && (
            <div className="mt-5 flex justify-center bg-white p-4 rounded-xl border border-falke-blue/10" data-testid="qr-display">
              <QRCodeSVG value={empfehlungsLink} size={180} fgColor="#2D5C7E" />
            </div>
          )}
        </div>
      </div>

      {/* Trophy preview */}
      <TrophyPreview />

      {/* Leaderboard — vorübergehend ausgeblendet (reaktivierbar via SHOW_LEADERBOARD=true) */}
      {false && (
        <div className="falke-card p-6">
          <Leaderboard compact />
        </div>
      )}

      {/* Gewerblich-Hinweis (wertneutral, ohne Prozent) */}
      {user.tippgeber_modus === "privat" && (
        <Link to="/app/profil?tab=gewerbe" data-testid="gewerbe-hint" className="block falke-card p-5 hover:shadow-[0_12px_40px_rgba(45,92,126,0.12)] transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading text-falke-blue">Du empfiehlst regelmäßig?</div>
              <div className="text-sm text-falke-anthracite/70">Werde gewerblicher Tippgeber – mit Geld-Vergütung pro Abschluss.</div>
            </div>
            <ArrowRight className="text-falke-orange" />
          </div>
        </Link>
      )}
    </div>
  );
}

function CoinCountUp({ value, duration = 900 }) {
  const [v, setV] = React.useState(0);
  React.useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setV(value);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{v}</>;
}

function TrophyPreview() {
  const [geschenke, setGeschenke] = useState([]);
  useEffect(() => {
    api.get("/geschenke").then((r) => setGeschenke(r.data));
  }, []);

  const top = geschenke.slice(0, 3);
  if (top.length === 0) return null;
  return (
    <div className="falke-card p-6" data-testid="trophy-preview">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg text-falke-blue flex items-center gap-2"><Gift size={20} /> Trophäen-Regal</h3>
        <Link to="/app/trophaeen" className="text-sm text-falke-orange font-semibold">Komplett ansehen</Link>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {top.map((g) => (
          <div key={g.id} className={`rounded-xl p-3 ${g.erreichbar ? "" : "opacity-40 grayscale"}`} data-testid={`preview-geschenk-${g.id}`}>
            {g.bild_url ? (
              <img src={resolveImageUrl(g.bild_url)} alt="" className="w-full aspect-square object-cover rounded-lg mb-2" />
            ) : (
              <div className="w-full aspect-square bg-falke-blue/5 rounded-lg mb-2 flex items-center justify-center"><Gift size={28} className="text-falke-blue/30" /></div>
            )}
            <div className="font-medium text-xs text-falke-anthracite truncate">{g.name}</div>
            <div className="text-xs text-falke-orange font-semibold tabular">{g.coin_preis} FC</div>
          </div>
        ))}
      </div>
    </div>
  );
}
